import * as React from 'react'
import { ArrowDown, ArrowUp, Plus, Trash2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import type { Json } from '@lib/helpers/json'
import { getList, getString, pathKey, setAt, type Draft, type Path } from './draft'
import type { DraftControl } from './fields'

/**
 * A price table: column headers, and rows of cells beneath them.
 *
 * **The headers decide the width, and the grid keeps every row to it.** Adding a
 * column appends a cell to every row; removing one takes that cell out of every
 * row; a new row arrives with exactly as many cells as there are headers. There
 * is no way to type a row of the wrong width, which is the rule the schema
 * enforces and the one two of the ten imported documents were breaking.
 *
 * That matters more than it sounds. Every cell is positional, so a row one cell
 * short does not look broken — it renders perfectly, with each price shifted one
 * column left of the heading it belongs under. Catching it at save time would be
 * late; not being able to express it is better.
 */
export function TableField({
  control,
  columnsPath,
  rowsPath,
  label,
}: {
  control: DraftControl
  columnsPath: Path
  rowsPath: Path
  label: string
}): React.ReactElement {
  const columns = getList(control.draft, columnsPath)
  const rows = getList(control.draft, rowsPath)
  const width = columns.length

  /** Rewrite every row so it is exactly `width` cells wide after a column change. */
  function withColumns(draft: Draft, nextColumns: Json[], changed: (row: Json[]) => Json[]): Draft {
    const nextRows = rows.map((row) => changed(Array.isArray(row) ? [...row] : []))
    return setAt(setAt(draft, columnsPath, nextColumns), rowsPath, nextRows)
  }

  function addColumn(): void {
    control.onChange(
      withColumns(control.draft, [...columns, ''], (row) => [...row, '']),
    )
  }

  function removeColumn(index: number): void {
    const nextColumns = columns.filter((_column, at) => at !== index)
    control.onChange(
      withColumns(control.draft, nextColumns, (row) => row.filter((_cell, at) => at !== index)),
    )
  }

  function addRow(): void {
    control.onChange(setAt(control.draft, rowsPath, [...rows, columns.map(() => '')]))
  }

  function removeRow(index: number): void {
    control.onChange(
      setAt(
        control.draft,
        rowsPath,
        rows.filter((_row, at) => at !== index),
      ),
    )
  }

  function moveRow(index: number, delta: number): void {
    const target = index + delta
    if (target < 0 || target >= rows.length) return
    const reordered = [...rows]
    const [moved] = reordered.splice(index, 1)
    if (moved === undefined) return
    reordered.splice(target, 0, moved)
    control.onChange(setAt(control.draft, rowsPath, reordered))
  }

  return (
    <div className="space-y-3">
      <div className="flex items-baseline justify-between gap-3">
        <span className="text-sm font-medium text-ink">{label}</span>
        <div className="flex items-center gap-1">
          <Button type="button" variant="ghost" size="sm" onClick={addColumn}>
            <Plus className="mr-1 size-3.5" aria-hidden />
            Column
          </Button>
          <Button type="button" variant="ghost" size="sm" disabled={width === 0} onClick={addRow}>
            <Plus className="mr-1 size-3.5" aria-hidden />
            Row
          </Button>
        </div>
      </div>

      {width === 0 ? (
        <p className="text-sm text-ink-muted">
          Add a column first — the headers decide how wide each row is.
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr>
                {columns.map((_column, index) => {
                  const headerPath = [...columnsPath, index]
                  return (
                    <th key={pathKey(headerPath)} className="p-1 text-left align-bottom">
                      <div className="flex items-center gap-1">
                        <Input
                          size="sm"
                          aria-label={`Column ${String(index + 1)} heading`}
                          placeholder="Heading"
                          value={getString(control.draft, headerPath)}
                          onChange={(event) => {
                            control.onChange(
                              setAt(control.draft, headerPath, event.target.value),
                            )
                          }}
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          aria-label={`Remove column ${String(index + 1)}`}
                          onClick={() => {
                            removeColumn(index)
                          }}
                        >
                          <Trash2 className="size-3.5 text-danger-ink" aria-hidden />
                        </Button>
                      </div>
                    </th>
                  )
                })}
                <th className="w-[1%]" />
              </tr>
            </thead>
            <tbody>
              {rows.map((_row, rowIndex) => {
                const rowPath = [...rowsPath, rowIndex]
                return (
                  <tr key={pathKey(rowPath)}>
                    {columns.map((_column, cellIndex) => {
                      const cellPath = [...rowPath, cellIndex]
                      return (
                        <td key={pathKey(cellPath)} className="p-1">
                          <Input
                            size="sm"
                            aria-label={`Row ${String(rowIndex + 1)}, column ${String(cellIndex + 1)}`}
                            value={getString(control.draft, cellPath)}
                            onChange={(event) => {
                              control.onChange(
                                setAt(control.draft, cellPath, event.target.value),
                              )
                            }}
                          />
                        </td>
                      )
                    })}
                    <td className="w-[1%] whitespace-nowrap p-1">
                      <div className="flex items-center gap-0.5">
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          aria-label={`Move row ${String(rowIndex + 1)} up`}
                          disabled={rowIndex === 0}
                          onClick={() => {
                            moveRow(rowIndex, -1)
                          }}
                        >
                          <ArrowUp className="size-3.5" aria-hidden />
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          aria-label={`Move row ${String(rowIndex + 1)} down`}
                          disabled={rowIndex === rows.length - 1}
                          onClick={() => {
                            moveRow(rowIndex, 1)
                          }}
                        >
                          <ArrowDown className="size-3.5" aria-hidden />
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          aria-label={`Remove row ${String(rowIndex + 1)}`}
                          onClick={() => {
                            removeRow(rowIndex)
                          }}
                        >
                          <Trash2 className="size-3.5 text-danger-ink" aria-hidden />
                        </Button>
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
      {rows.length === 0 && width > 0 ? (
        <p className="text-sm text-ink-muted">No rows yet.</p>
      ) : null}
    </div>
  )
}
